/** The subset of Clerk's SessionResource needed by the transport. */
type TokenSession = {
  id: string;
  getToken: (options: { skipCache: boolean }) => Promise<string | null>;
};

function currentTokenSession(): TokenSession | null {
  if (typeof window === 'undefined') return null;
  return (window as unknown as { Clerk?: { session?: TokenSession | null } }).Clerk?.session ?? null;
}

const refreshes = new Map<string, Promise<string | null>>();

export function tokenSessionForRead(token: string): TokenSession | null {
  const session = currentTokenSession();
  if (!session) return null;
  try {
    // Clerk's default JWT sid is the session ID (not the user's ID). Parsing
    // here only restricts retries; the server still verifies authentication.
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.sid === session.id ? session : null;
  } catch {
    return null;
  }
}

/**
 * A 401 can mean a cached token expired while the Clerk session is still alive.
 * Ask Clerk for a fresh token; never sign out on a refusal (signup bootstrap
 * also returns 401) or on a connection failure. SessionResource.getToken's
 * skipCache option is present in the installed Clerk 5.61.3 types.
 */
export async function refreshReadToken(session: TokenSession, signal?: AbortSignal | null): Promise<string | null> {
  if (currentTokenSession()?.id !== session.id) return null;
  if (signal?.aborted) throw signal.reason;

  let refresh = refreshes.get(session.id);
  if (!refresh) {
    refresh = session.getToken({ skipCache: true })
      .catch(() => null)
      .finally(() => { if (refreshes.get(session.id) === refresh) refreshes.delete(session.id); });
    refreshes.set(session.id, refresh);
  }

  // Clerk owns its network request; our caller can stop waiting without
  // cancelling the refresh that another simultaneous read still needs.
  const token = signal ? await new Promise<string | null>((resolve, reject) => {
    const onAbort = () => {
      signal.removeEventListener('abort', onAbort);
      // A later Try again must be able to start a new refresh if this one hung.
      if (refreshes.get(session.id) === refresh) refreshes.delete(session.id);
      reject(signal.reason);
    };
    signal.addEventListener('abort', onAbort, { once: true });
    if (signal.aborted) onAbort();
    refresh.then(value => {
      signal.removeEventListener('abort', onAbort);
      resolve(value);
    });
  }) : await refresh;

  // A request for the old account must never be replayed as the new account.
  return currentTokenSession()?.id === session.id ? token : null;
}
